import React, {useState, useEffect} from 'react'
import {getAll} from '../../../../app/services/common_requests'
import {ID} from '../../../helpers'
import {useIntl} from 'react-intl'

interface TagsComponentProps {
  initialValues: string[]
  onValuesChange: (values: string[]) => void
  type: 'themes' | 'options' | 'metaKeywords'
  selectedLanguage: string
}

const TagsComponent: React.FC<TagsComponentProps> = ({
  initialValues,
  onValuesChange,
  type,
  selectedLanguage,
}) => {
  const [values, setValues] = useState<string[]>([])
  const [availableValues, setAvailableValues] = useState<Array<{id: string; name: string}>>([])
  const [selectedValue, setSelectedValue] = useState<string>('')
  const [newKeyword, setNewKeyword] = useState<string>('')
  const intl = useIntl()

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues.filter((val) => typeof val === 'string'))
    }
  }, [initialValues, selectedLanguage])

  useEffect(() => {
    if (type === 'themes') {
      getAll({language: selectedLanguage}, '/themes').then((data) => {
        setAvailableValues(
          data.data.map((theme: {_id: ID; translations: {name: string}[]}) => ({
            id: String(theme._id),
            name: theme.translations[0]?.name || '',
          }))
        )
      })
    } else if (type === 'options') {
      getAll({language: selectedLanguage}, '/options').then((response) => {
        setAvailableValues(
          response.data.map((option: {_id: ID; translations: {title: string}[]}) => ({
            id: String(option._id),
            name: option.translations[0]?.title || '',
          }))
        )
      })
    }
  }, [type, selectedLanguage])

  const handleAddValue = () => {
    if (type === 'metaKeywords') {
      const trimmedKeyword = newKeyword.trim()

      if (trimmedKeyword && !values.includes(trimmedKeyword)) {
        const updatedValues = [...values, trimmedKeyword]
        setValues(updatedValues)
        onValuesChange(updatedValues)
        setNewKeyword('')
      }
    } else {
      if (selectedValue.trim() && !values.includes(selectedValue.trim())) {
        const updatedValues = [...values, selectedValue.trim()]
        setValues(updatedValues)
        onValuesChange(updatedValues)
        setSelectedValue('')
      }
    }
  }

  const handleDeleteValue = (index: number) => {
    const updatedValues = values.filter((_, i) => i !== index)
    setValues(updatedValues)
    onValuesChange(updatedValues)
  }

  return (
    <div>
      <div className='mb-3'>
        {values.map((id, index) => {
          const item = availableValues.find((v) => v.id === id)
          return (
            <span key={index} className='badge badge-light me-2'>
              {item?.name || id}
              <button
                type='button'
                className='btn-close ms-2'
                aria-label='Delete'
                onClick={() => handleDeleteValue(index)}
              />
            </span>
          )
        })}
      </div>

      <div className='d-flex align-items-center mb-3'>
        {type === 'metaKeywords' ? (
          <>
            <input
              type='text'
              className='form-control me-2 w-50'
              placeholder={intl.formatMessage({
                id: 'COLUMNS.SELECT_SEO_KEYWORDS',
              })}
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newKeyword.trim()) {
                  handleAddValue()
                  e.preventDefault()
                }
              }}
            />
            <button type='button' className='btn btn-primary' onClick={handleAddValue}>
              {' '}
              {intl.formatMessage({
                id: 'COLUMNS.SELECT_SEO_KEYWORDS',
              })}
            </button>
          </>
        ) : (
          <>
            <select
              className='form-select me-2 w-50'
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
            >
              <option value=''>
                {type === 'themes'
                  ? intl.formatMessage({id: 'COLUMNS.SELECT_THEME'})
                  : intl.formatMessage({id: 'COLUMNS.SELECT_OPTION'})}
              </option>
              {availableValues
                .filter((item) => !values.includes(item.id))
                .map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
            <button type='button' className='btn btn-primary' onClick={handleAddValue}>
              {type === 'themes'
                ? intl.formatMessage({id: 'COLUMNS.ADD_THEME'})
                : intl.formatMessage({id: 'COLUMNS.ADD_OPTION'})}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default TagsComponent
